import React, { useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';

import { FocusDiv } from '@accedo/vdkweb-tv-ui';
import { focusManager } from '@accedo/vdkweb-navigation';
import { vKey } from '@accedo/xdk-virtual-key';

import { navIdMap as NAV_IDS } from '#/utils/navigationHelper';
import { screenView } from '#/services/analytics';
import usePage from '#/hooks/usePage';
import useKeyHandler from '#/hooks/useKeyHandler';

/**
 * Page component which uses a custom hook for updating the information
 * of the current focused page.
 */

let isGoingBack = false;
const MENU_NAV = NAV_IDS.MENU.HEADER.MENU_ITEM;

const Page = ({
  autoFocus = true,
  className,
  children,
  id,
  nav,
  onBackwardDidMount,
  onBackwardWillUnmount,
  onForwardDidMount,
  onForwardWillUnmount,
  restoreLastFocusedElementOnMount,
  saveFocusedElementOnUnmount,
  analyticsProps
}) => {
  const {
    getPageLatestStateById,
    setPageLatestStateById,
    setCurrentPageId
  } = usePage();

  const keyHandler = useCallback(({ id: keyId }) => {
    const { BACK } = vKey;

    if (keyId === BACK.id) {
      isGoingBack = true;
    }
  }, []);

  useKeyHandler(keyHandler);

  /**
   * Default function to save which element was focused when the Page is unmounted from the DOM.
   *
   * If the item focused is in a container, it will mark the container it self as
   * last focused to avoid issues when item are no longer present
   */
  const saveCurrentFocusedElement = useCallback(() => {
    const currentTrail = focusManager.getTrail();
    const containerLayoutIndex = currentTrail.findIndex(
      item => item === NAV_IDS.PAGE.CONTAINER.LAYOUT
    );
    const indextoSetBack =
      containerLayoutIndex > 0 ? containerLayoutIndex - 1 : 0;
    const currentFocus = currentTrail[indextoSetBack];
    const previousHistoryState = {};

    previousHistoryState.focusedElement = currentFocus;

    setPageLatestStateById(id, previousHistoryState);
  }, [setPageLatestStateById, id]);

  /**
   * Default function to restore the last element focussed
   * when the Page is mounted back with a back navigation.
   */
  const restoreLastFocusedElement = useCallback(
    latestState => {
      if (
        latestState?.focusedElement &&
        // to avoid the focus is on a menu button when we go back between views
        !latestState?.focusedElement.includes(`${MENU_NAV}`)
      ) {
        focusManager.changeFocus(latestState.focusedElement);
      } else {
        focusManager.changeFocus(id);
      }
    },
    [id]
  );

  // we only want to execute it on the first and last render
  useEffect(() => {
    if (autoFocus) {
      focusManager.changeFocus(id);
    }
    // we need the latest update of isGoingBack, we cannot store the valeue
    if (isGoingBack) {
      // onBackwardDidMount
      const latestState = getPageLatestStateById(id);
      if (restoreLastFocusedElementOnMount) {
        restoreLastFocusedElement(latestState);
      }
      onBackwardDidMount && onBackwardDidMount(latestState);
    } else {
      onForwardDidMount && onForwardDidMount();
    }
    setCurrentPageId(id);
    isGoingBack = false;
    return () => {
      // Page will be unmount from dom
      // we need the latest update
      if (isGoingBack) {
        // onBackwardDidMount
        onBackwardWillUnmount && onBackwardWillUnmount(id);
      } else {
        if (saveFocusedElementOnUnmount) {
          saveCurrentFocusedElement();
        }
        onForwardWillUnmount && onForwardWillUnmount(id);
      }
      setCurrentPageId(null);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (analyticsProps?.screenTitle) {
      screenView(analyticsProps);
    }
  }, [analyticsProps]);

  Page.propTypes = {
    autoFocus: PropTypes.bool,
    className: PropTypes.string,
    children: PropTypes.node,
    id: PropTypes.string.isRequired,
    nav: PropTypes.object,
    /** Callback function to be called on backward navigation mount */
    onBackwardDidMount: PropTypes.func,

    /** Callback function to be called on backward navigation unmount */
    onBackwardWillUnmount: PropTypes.func,

    /** Callback function to be called on forward navigation mount */
    onForwardDidMount: PropTypes.func,

    /** Callback function to be called on forward navigation unmount */
    onForwardWillUnmount: PropTypes.func,

    /** To restore the last focused element on backward mount */
    restoreLastFocusedElementOnMount: PropTypes.bool,

    /** To save the latest focused elemnt on unmount */
    saveFocusedElementOnUnmount: PropTypes.bool,

    analyticsProps: PropTypes.shape({
      /** Analytics screenTitle prop: Title of the page */
      screenTitle: PropTypes.string,
      /** Analytics screenType prop: Type of the page */
      screenType: PropTypes.string,
      /** Asset Properties */
      contentId: PropTypes.string,
      title: PropTypes.string,
      description: PropTypes.string,
      publishDate: PropTypes.string,
      seasonNumber: PropTypes.number,
      episodeNumber: PropTypes.number,
      genre: PropTypes.string,
      duration: PropTypes.string
    })
  };

  return (
    <FocusDiv className={className} nav={nav}>
      {children}
    </FocusDiv>
  );
};

export default Page;
